import React, { useState } from 'react';
import { ChevronDown, ChevronUp, GraduationCap, Calculator, CreditCard, Calendar } from 'lucide-react';

interface Fees {
  tuition: number;
  registration: number;
  books: number;
  activity: number;
  transport: number;
  lunch: number;
}

interface GradeInfo {
  grade: string;
  icon: string;
  fees: Fees;
}

interface PaymentPlan {
  id: string;
  name: string;
  discount: number;
  label: string;
}

const SchoolFeeStructure: React.FC = () => {
  const [expandedGrade, setExpandedGrade] = useState<number | null>(null);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<string>('annual');

  const feeStructure: GradeInfo[] = [
    {
      grade: 'Pre-K (Ages 3-4)',
      icon: '🎨',
      fees: {
        tuition: 12000,
        registration: 500,
        books: 300,
        activity: 800,
        transport: 2400,
        lunch: 1800
      }
    },
    {
      grade: 'Elementary (K-5)',
      icon: '📚',
      fees: {
        tuition: 15000,
        registration: 600,
        books: 500,
        activity: 1000,
        transport: 2400,
        lunch: 1800
      }
    },
    {
      grade: 'Middle School (6-8)',
      icon: '🔬',
      fees: {
        tuition: 18000,
        registration: 700,
        books: 700,
        activity: 1200,
        transport: 2400,
        lunch: 1800
      }
    },
    {
      grade: 'High School (9-12)',
      icon: '🎓',
      fees: {
        tuition: 22000,
        registration: 800,
        books: 900,
        activity: 1500,
        transport: 2400,
        lunch: 1800
      }
    }
  ];

  const paymentPlans: PaymentPlan[] = [
    { id: 'annual', name: 'Annual Payment', discount: 0.05, label: '5% Discount' },
    { id: 'semester', name: 'Semester Payment', discount: 0.02, label: '2% Discount' },
    { id: 'monthly', name: 'Monthly Payment', discount: 0, label: 'No Discount' }
  ];

  const calculateTotal = (fees: Fees): number => {
    return Object.values(fees).reduce((sum, fee) => sum + fee, 0);
  };

  const applyDiscount = (amount: number, discount: number): number => {
    return amount * (1 - discount);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const toggleGrade = (index: number): void => {
    setExpandedGrade(expandedGrade === index ? null : index);
  };

  return (
    <div className="w-full mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex justify-center items-center mb-4">
          <GraduationCap className="w-12 h-12 text-indigo-600 mr-3" />
          <h1 className="text-4xl font-bold text-gray-800">Ever Green Sr. Sec. School</h1>
        </div>
        <p className="text-xl text-gray-600 mb-6">Academic Year 2024-2025 Fee Structure</p>
        
        {/* Payment Plan Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-2 shadow-md">
            {paymentPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPaymentPlan(plan.id)}
                className={`px-4 py-2 rounded-md mr-2 last:mr-0 transition-all ${
                  selectedPaymentPlan === plan.id
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="font-medium">{plan.name}</div>
                <div className="text-sm opacity-75">{plan.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Fee Structure Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
        {feeStructure.map((gradeInfo: GradeInfo, index: number) => {
          const total: number = calculateTotal(gradeInfo.fees);
          const selectedPlan: PaymentPlan | undefined = paymentPlans.find(p => p.id === selectedPaymentPlan);
          const discountedTotal: number = selectedPlan ? applyDiscount(total, selectedPlan.discount) : total;
          const savings: number = total - discountedTotal;
          
          return (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
              {/* Card Header */}
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 cursor-pointer"
                onClick={() => toggleGrade(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-3xl mr-3">{gradeInfo.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold text-white">{gradeInfo.grade}</h3>
                      <div className="flex items-center mt-1">
                        <Calculator className="w-4 h-4 text-white mr-1" />
                        <span className="text-white font-medium">{formatCurrency(discountedTotal)}</span>
                        {savings > 0 && (
                          <span className="ml-2 bg-green-400 text-green-900 px-2 py-1 rounded-full text-xs font-medium">
                            Save {formatCurrency(savings)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedGrade === index ? 
                    <ChevronUp className="w-6 h-6 text-white" /> : 
                    <ChevronDown className="w-6 h-6 text-white" />
                  }
                </div>
              </div>

              {/* Card Content */}
              {expandedGrade === index && (
                <div className="p-6 space-y-4">
                  {/* Fee Breakdown */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 mb-3">Fee Breakdown</h4>
                    {Object.entries(gradeInfo.fees).map(([feeType, amount]: [string, number]) => (
                      <div key={feeType} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 capitalize">{feeType.replace(/([A-Z])/g, ' $1')}</span>
                        <span className="font-medium text-gray-800">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Total Section */}
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">{formatCurrency(total)}</span>
                    </div>
                    {selectedPlan && selectedPlan.discount > 0 && (
                      <div className="flex justify-between items-center mb-2 text-green-600">
                        <span>Discount ({selectedPlan ? Math.round(selectedPlan.discount * 100) : 0}%)</span>
                        <span>-{formatCurrency(savings)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-lg font-bold text-indigo-600 pt-2 border-t border-gray-200">
                      <span>Total Annual Fee</span>
                      <span>{formatCurrency(discountedTotal)}</span>
                    </div>
                  </div>

                  {/* Payment Schedule */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="font-medium text-blue-800">Payment Schedule</span>
                    </div>
                    <div className="text-sm text-blue-700">
                      {selectedPaymentPlan === 'annual' && 'Due: August 1st (5% discount applied)'}
                      {selectedPaymentPlan === 'semester' && 'Due: August 1st & January 1st (2% discount applied)'}
                      {selectedPaymentPlan === 'monthly' && 'Due: 1st of each month (10 payments)'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Additional Information */}
      <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <CreditCard className="w-6 h-6 mr-2 text-indigo-600" />
          Important Information
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Payment Methods</h4>
            <ul className="text-gray-600 space-y-2">
              <li>• Bank transfer (ACH)</li>
              <li>• Credit/Debit cards</li>
              <li>• Check payments</li>
              <li>• Online payment portal</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Additional Notes</h4>
            <ul className="text-gray-600 space-y-2">
              <li>• Registration fee is non-refundable</li>
              <li>• Sibling discount: 10% for second child</li>
              <li>• Late payment fee: 50 Rupees</li>
              <li>• Financial aid available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolFeeStructure;